// api.service.ts
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  private AUTH_API = `${environment.authAPIUrl}`;
  private AI_API = `${environment.aiAPIUrl}`;
  private EXPLORATOR_API = `${environment.exploratorAPIUrl}`;

  /* AUTH API */

  getRegistrationUrl = (): string => `${this.AUTH_API}/users/register/`;

  getLoginUrl = (): string => `${this.AUTH_API}/auth/login/`;

  getSwitchWorkspaceUrl = (): string => `${this.AUTH_API}/auth/relogin/`;

  getRefreshTokenUrl = (): string => `${this.AUTH_API}/auth/refresh/`;

  getUserDetailUrl = (uuid: string): string => `${this.AUTH_API}/users/${uuid}/?extra=companies`;

  getDeleteUserUrl = (uuid: string, isAnonymized: boolean = false): string =>
    `${this.AUTH_API}/users/${uuid}/${isAnonymized ? '?is_anonymized=true' : ''}`;

  getPasswordResetUrl = (): string => `${this.AUTH_API}/users/send_reset_password_email/`;

  getPasswordResetConfirmUrl = (signature: string): string =>
    `${this.AUTH_API}/users/reset_password/?signature=${signature}`;

  getPasswordChangeUrl = (uuid: string): string => `${this.AUTH_API}/users/${uuid}/set_password/`;

  logoutURL = (): string => `${this.AUTH_API}/auth/logout/`;

  getWorkspaceDetails = (companyUuid: string): string => `${this.AUTH_API}/companies/${companyUuid}/`;

  checkTurnstileTokenUrl = (): string => `${this.AUTH_API}/turnstile_verification/`;

  getFeedjoltTokenUrl = (): string => `${this.AUTH_API}/feedjolt_identify/`;

  getTeamMembersUrl = (uuid: string): string => `${this.AUTH_API}/users/?company=${uuid}`;

  addTeamMemberUrl = (): string => `${this.AUTH_API}/users/invite/`;

  resendInviteEmailUrl = (uuid: string): string => `${this.AUTH_API}/users/${uuid}/send_invite_email/`;

  getUserDevicesUrl = (uuid: string): string => `${this.AUTH_API}/users/${uuid}/?extra=devices`;

  getWorkspaceAvailableConnectorsUrl = (companyUuid: string): string =>
    `${this.AUTH_API}/companies/${companyUuid}/?extra=connectors`;

  getWorkspacesUrl = (): string => `${this.AUTH_API}/companies/`;

  getActivateWorkspaceUrl = (): string => `${this.AUTH_API}/companies/activate/`;

  getWorkspaceSettingsUrl = (companyUuid: string): string => `${this.AUTH_API}/companies/${companyUuid}/settings/`;

  getTokenHashesUrl = (): string => `${this.AUTH_API}/tokenhashes/`;

  getUserTokenHashesUrl = (uuid: string, companyUuid: string): string =>
    `${this.AUTH_API}/tokenhashes/?user=${uuid}&company=${companyUuid}`;

  /* BILLING API */

  getPricesUrl = (): string => `${this.AUTH_API}/stripe/get_prices/`;

  getSubscriptionsUrl = (companyUuid: string): string =>
    `${this.AUTH_API}/companies/${companyUuid}/?extra=subscription`;

  getInvoicesUrl = (companyUuid: string): string => `${this.AUTH_API}/companies/${companyUuid}/?extra=invoices`;

  getSubscribeUrl = (): string => `${this.AUTH_API}/stripe/get_session/`;

  updateSubscriptionUrl = (): string => `${this.AUTH_API}/stripe/update_subscription/`;

  getCheckoutSessionUrl = (): string => `${this.AUTH_API}/stripe/get_checkout_session/`;

  getStripePortalSessionUrl = (): string => `${this.AUTH_API}/stripe/get_portal_session/`;

  getPromoCodeUrl = (code: string): string => `${this.AUTH_API}/stripe/get_coupon/?promotion_code=${code}`;

  updateSubscriptionStatusUrl = (): string => `${this.AUTH_API}/stripe/update_subscription_status/`;

  /* EXPLORATOR API */

  getCompanyConnectorsUrl = (): string => `${this.EXPLORATOR_API}/companyconnectors/`;

  getCompanyConnectorsEnableUrl = (connectorUuid: string): string =>
    `${this.EXPLORATOR_API}/companyconnectors/${connectorUuid}/set_enabled/`;

  getCompanyConnectorsDisableUrl = (connectorUuid: string): string =>
    `${this.EXPLORATOR_API}/companyconnectors/${connectorUuid}/set_enabled/`;

  getConnectorDetailsUrl = (connectorUuid: string): string =>
    `${this.EXPLORATOR_API}/companyconnectors/${connectorUuid}/`;

  getSearchUrl = (query: string): string => `${this.EXPLORATOR_API}/search/`;

  getExportResultsUrl = (searchId: string): string => `${this.EXPLORATOR_API}/search/${searchId}/export/`;

  getSearchResultsUrl = (searchId: string): string => `${this.EXPLORATOR_API}/search/results/?hub=${searchId}`;

  getLastQueriesUrl = (): string => `${this.EXPLORATOR_API}/latest-user-queries/`;

  getSearchWebsocketTokenUrl = (hubId: string): string => `${this.EXPLORATOR_API}/search/ws_negotiate/?hub=${hubId}`;

  getWorkspaceConnectorsUrl = (companyUuid: string): string =>
    `${this.EXPLORATOR_API}/companyconnectors/?company=${companyUuid}`;

  getConnectorsUrl = (): string => `${this.EXPLORATOR_API}/connectors/`;

  geWorkspaceConnectorCopyUrl = (): string => `${this.EXPLORATOR_API}/companyconnectors/copy/`;

  getCompanyConnectorsAllToggleUrl = (): string => `${this.EXPLORATOR_API}/companyconnectors/set_enabled/`;

  /* AI API */
  getSpaceWebsocketTokenUrl = (hubId: string): string => `${this.AI_API}/spaces/ws_negotiate/?hub=${hubId}`;

  getUserSpacesUrl = (uuid: string): string => {
    const fields = ['name', 'record_count', 'role', 'updated_at', 'is_my_space', 'uuid', 'tags', 'is_archived'];
    const fieldsQuery = fields.map((f) => `fields=${encodeURIComponent(f)}`).join('&');
    return `${this.AI_API}/spaces/?user=${uuid}&${fieldsQuery}`;
  };

  getSpaceUrl = (): string => `${this.AI_API}/spaces/`;

  getSpaceDetailsUrl = (spaceId: string): string => `${this.AI_API}/spaces/${spaceId}/`;

  getSpaceUsersUrl = (spaceId: string): string => `${this.AI_API}/spaces/${spaceId}/?extra=company_users`;

  getSpaceUsersManagementUrl = (): string => `${this.AI_API}/spaceusers/`;

  getSpaceRecordsUrl = (spaceId: string): string => `${this.AI_API}/records/?space=${spaceId}`;

  getSpacesByRecordHashUrl = (): string => `${this.AI_API}/spaces/get_by_record_hash/`;

  getSpaceRecordDetailsUrl = (recordId: string): string => `${this.AI_API}/records/${recordId}/`;

  getRecordsUrl = (): string => `${this.AI_API}/records/`;

  getChatsUrl = (): string => `${this.AI_API}/chats/`;

  getSpaceChatsUrl = (spaceId: string): string => `${this.AI_API}/chats/?space=${spaceId}`;

  getChatMessagesUrl = (chatId: string): string => `${this.AI_API}/messages/?chat=${chatId}`;

  getPinnedMessagesUrl = (spaceUuid: string): string => `${this.AI_API}/messages/?space=${spaceUuid}&is_pinned=true`;

  getChatMessagesResponseUrl = (chatId: string): string => `${this.AI_API}/messages/get_response/?chat=${chatId}`;

  getMessageDetailsUrl = (messageId: string): string => `${this.AI_API}/messages/${messageId}/`;

  getUpdatedMessageResponseUrl = (messageId: string): string =>
    `${this.AI_API}/messages/${messageId}/get_updated_response/`;

  getMessageToAudioUrl = (messageId: string): string => `${this.AI_API}/messages/${messageId}/get_speech_from_text/`;

  getAudioToTextUrl = (): string => `${this.AI_API}/messages/get_text_from_speech/`;

  getChatFileUploadUrl = (): string => `${this.AI_API}/messages/upload_file/`;

  cloneSpaceUrl = (): string => `${this.AI_API}/spaces/clone/`;

  getTagsUrl = (): string => `${this.AI_API}/tags/`;

  getUserTagsUrl = (userId: string): string => `${this.AI_API}/tags/?user=${userId}`;

  resultActionUrl = (id: string): string => `${this.AI_API}/records/summarize/`;

  /* AI API — Agents */
  getAgentsUrl = (): string => `${this.AI_API}/agents/`;
  getAgentDetailUrl = (uuid: string): string => `${this.AI_API}/agents/${uuid}/`;
  getAgentDuplicateUrl = (uuid: string): string => `${this.AI_API}/agents/${uuid}/duplicate/`;
  getAgentAvatarUploadUrl = (uuid: string): string => `${this.AI_API}/agents/${uuid}/upload-avatar/`;
  getAgentModelCatalogUrl = (): string => `${this.AI_API}/agents/model-catalog/`;
  getAgentToolCatalogUrl = (): string => `${this.AI_API}/agents/tool-catalog/`;
  getAgentCategoriesUrl = (): string => `${this.AI_API}/agents/categories/`;
  getAgentCategoryDetailUrl = (uuid: string): string => `${this.AI_API}/agents/categories/${uuid}/`;

  /* AI API — Space Bots */

  getSpaceBotsUrl = (spaceUuid?: string): string =>
    spaceUuid ? `${this.AI_API}/spacebots/?space=${spaceUuid}` : `${this.AI_API}/spacebots/`;
  getSpaceBotDetailUrl = (uuid: string): string => `${this.AI_API}/spacebots/${uuid}/`;
  getSpaceBotRunUrl = (uuid: string): string => `${this.AI_API}/spacebots/${uuid}/run/`;
  getSpaceBotPauseUrl = (uuid: string): string => `${this.AI_API}/spacebots/${uuid}/pause/`;
  getSpaceBotResumeUrl = (uuid: string): string => `${this.AI_API}/spacebots/${uuid}/resume/`;
  getSpaceBotMarkReadUrl = (uuid: string): string => `${this.AI_API}/spacebots/${uuid}/mark_read/`;
  getSpaceBotVersionsUrl = (botUuid: string): string => `${this.AI_API}/spacebotversions/?bot=${botUuid}`;
  getSpaceBotVersionDetailUrl = (uuid: string): string => `${this.AI_API}/spacebotversions/${uuid}/`;
}
